import { useEffect, useState } from 'react';
import { t, tn, type MessageKey } from '../i18n/index.ts';
import { action, subscribe } from '../lib/api.ts';
import { useApprovalCount } from '../lib/approvals.ts';
import { useOnlinePending } from '../lib/online.ts';
import { usePlaces } from '../lib/places.tsx';
import { useSession } from '../lib/session.tsx';
import { SECTIONS } from '../screens/manage/common.tsx';
import type { CalendarSoon } from './CalendarReminders.tsx';
import type { IconName } from './Icon.tsx';

/**
 * The one list of places in the app: the sidebar, the phone menu, the application launcher and
 * the command search all read it. Each entry is shown only with its permission (Golden Rule 9);
 * badges count what waits for this person, live.
 */

export type NavGroup = 'industry' | 'crm' | 'sales' | 'purchases' | 'inventory' | 'finance' | 'hr' | 'projects' | 'operations' | 'documents' | 'collab' | 'reports' | 'settings';

/** In the order of the brief's navigation; "industry" holds the vertical solutions (restaurant, hotel). */
export const GROUPS: { key: NavGroup; label: MessageKey; icon: IconName }[] = [
  { key: 'industry', label: 'navg.industry', icon: 'store' },
  { key: 'crm', label: 'navg.crm', icon: 'users' },
  { key: 'sales', label: 'navg.sales', icon: 'trendUp' },
  { key: 'purchases', label: 'navg.purchases', icon: 'bag' },
  { key: 'inventory', label: 'navg.inventory', icon: 'box' },
  { key: 'finance', label: 'navg.finance', icon: 'bank' },
  { key: 'hr', label: 'navg.hr', icon: 'contact' },
  { key: 'projects', label: 'navg.projects', icon: 'layers' },
  { key: 'operations', label: 'navg.operations', icon: 'factory' },
  { key: 'documents', label: 'navg.documents', icon: 'folder' },
  { key: 'collab', label: 'navg.collab', icon: 'messages' },
  { key: 'reports', label: 'navg.reports', icon: 'chart' },
  { key: 'settings', label: 'navg.settings', icon: 'settings' },
];

export interface NavEntry {
  to: string;
  group: NavGroup;
  icon: IconName;
  label: string;
  show: boolean;
  badge?: number;
  badgeLabel?: string;
}

/** Every place this person may open, with live badges, plus the calendar's coming reminders. */
export function useNavEntries(): { entries: NavEntry[]; calendarSoon: CalendarSoon[] } {
  const { can } = useSession();
  const { here, multi } = usePlaces();
  const online = useOnlinePending(can('online.handle'), multi && here ? here.id : null);
  const onlineCount = online.requests + online.calls;
  const approvals = useApprovalCount(can('studio.use'));
  const shopNew = useShopPending(can('shop.handle'));
  const chatWaiting = useChatPending(can('livechat.answer'));
  const discussNew = useDiscussPending(can('discuss.use'));
  const smsNew = useSmsPending(can('sms.view'));
  const socialDue = useSocialPending(can('social.view'));
  const todoDue = useTodoDue(can('todo.use'));
  const learnDue = useLearningDue(can('elearning.learn'));
  const iotAlerts = useIotAlerts(can('iot.view'));
  const callsWaiting = useCallsPending(can('voip.view'));
  const helpWaiting = useHelpdeskPending(can('helpdesk.view'));
  const calendar = useCalendarPending(can('calendar.use'));
  const calendarNew = calendar.invitations;
  const tabs: NavEntry[] = [
    { to: '/pos', group: 'industry', icon: 'table', label: t('nav.tables'), show: can('restaurant.order') },
    { to: '/till', group: 'industry', icon: 'scan', label: t('nav.till'), show: can('restaurant.order') },
    {
      to: '/online',
      group: 'industry',
      icon: 'qr',
      label: t('nav.online'),
      show: can('online.handle') && (online.enabled || onlineCount > 0),
      badge: onlineCount,
      badgeLabel: tn('nav.online_waiting', onlineCount),
    },
    { to: '/kitchen', group: 'industry', icon: 'chef', label: t('nav.kitchen'), show: can('restaurant.kitchen') },
    { to: '/hotel', group: 'industry', icon: 'bed', label: t('nav.hotel'), show: can('hotel.view') },
    { to: '/manufacturing', group: 'operations', icon: 'factory', label: t('nav.manufacturing'), show: can('mrp.view') },
    { to: '/shop', group: 'sales', icon: 'cart', label: t('nav.shop'), show: can('shop.view'), badge: shopNew, badgeLabel: tn('nav.shop_waiting', shopNew) },
    { to: '/rental', group: 'sales', icon: 'key', label: t('nav.rental'), show: can('rental.view') },
    { to: '/subscriptions', group: 'sales', icon: 'undo', label: t('nav.subscriptions'), show: can('recurring.view') },
    { to: '/marketing', group: 'crm', icon: 'send', label: t('nav.marketing'), show: can('marketing.view') },
    { to: '/outreach', group: 'crm', icon: 'contact', label: t('nav.outreach'), show: can('outreach.view') },
    { to: '/sms', group: 'crm', icon: 'messages', label: t('nav.sms'), show: can('sms.view'), badge: smsNew, badgeLabel: tn('nav.sms_unread', smsNew) },
    { to: '/social', group: 'crm', icon: 'share', label: t('nav.social'), show: can('social.view'), badge: socialDue, badgeLabel: tn('nav.social_waiting', socialDue) },
    { to: '/calls', group: 'crm', icon: 'phone', label: t('nav.calls'), show: can('voip.view'), badge: callsWaiting, badgeLabel: tn('nav.calls_waiting', callsWaiting) },
    { to: '/mailbox', group: 'crm', icon: 'inbox', label: t('nav.mailbox'), show: can('mailbox.view') },
    { to: '/helpdesk', group: 'crm', icon: 'lifebuoy', label: t('nav.helpdesk'), show: can('helpdesk.view'), badge: helpWaiting, badgeLabel: tn('nav.helpdesk_waiting', helpWaiting) },
    { to: '/field', group: 'projects', icon: 'pin', label: t('nav.field'), show: can('field.view') },
    { to: '/tasks', group: 'projects', icon: 'list', label: t('nav.tasks'), show: can('project.view') },
    { to: '/shift', group: 'industry', icon: 'register', label: t('nav.shift'), show: can('pos.shift') },
    { to: '/receipts', group: 'industry', icon: 'receipt', label: t('nav.receipts'), show: can('pos.shift') && can('sales.view') },
    { to: '/guests', group: 'crm', icon: 'calendar', label: t('nav.guests'), show: can('reservations.view') || can('customers.view') },
    { to: '/reports', group: 'reports', icon: 'chart', label: t('nav.reports'), show: can('reports.view') },
    { to: '/invoices', group: 'sales', icon: 'invoice', label: t('nav.invoices'), show: can('invoicing.view') },
    { to: '/payments', group: 'finance', icon: 'card', label: t('nav.payments'), show: can('payments.view') },
    { to: '/shipping', group: 'sales', icon: 'truck', label: t('nav.shipping'), show: can('shipping.view') },
    { to: '/accounting', group: 'finance', icon: 'book', label: t('nav.accounting'), show: can('accounting.view') },
    { to: '/bank', group: 'finance', icon: 'bank', label: t('nav.bank'), show: can('bank.view') },
    { to: '/crm', group: 'crm', icon: 'trendUp', label: t('nav.crm'), show: can('crm.use') },
    { to: '/projects', group: 'projects', icon: 'layers', label: t('nav.projects'), show: can('construction.view') },
    { to: '/routes', group: 'sales', icon: 'truck', label: t('nav.distribution'), show: can('distribution.field') || can('distribution.cash') },
    { to: '/payroll', group: 'hr', icon: 'cash', label: t('nav.payroll'), show: can('payroll.view') },
    { to: '/insights', group: 'finance', icon: 'sparkle', label: t('nav.insights'), show: can('ai.use') },
    { to: '/budget', group: 'finance', icon: 'bank', label: t('nav.budget'), show: can('budget.view') },
    { to: '/consolidation', group: 'finance', icon: 'branch', label: t('nav.consolidation'), show: can('consolidation.view') },
    { to: '/esg', group: 'operations', icon: 'leaf', label: t('nav.esg'), show: can('esg.view') },
    { to: '/iot', group: 'operations', icon: 'cpu', label: t('nav.iot'), show: can('iot.view'), badge: iotAlerts, badgeLabel: tn('nav.iot_alerts', iotAlerts) },
    { to: '/protocol', group: 'documents', icon: 'book', label: t('nav.protocol'), show: can('protocol.view') },
    { to: '/assets', group: 'finance', icon: 'archive', label: t('nav.assets'), show: can('assets.view') },
    { to: '/procurement', group: 'purchases', icon: 'list', label: t('nav.procurement'), show: can('procurement.view') },
    { to: '/team', group: 'hr', icon: 'clock', label: t('nav.team'), show: can('team.self') },
    { to: '/people', group: 'hr', icon: 'users', label: t('nav.people'), show: can('hr.self') },
    { to: '/documents', group: 'documents', icon: 'folder', label: t('nav.documents'), show: can('doc.use') },
    { to: '/fleet', group: 'operations', icon: 'truck', label: t('nav.fleet'), show: can('fleet.drive') },
    { to: '/todo', group: 'collab', icon: 'check', label: t('nav.todo'), show: can('todo.use'), badge: todoDue, badgeLabel: tn('nav.todo_waiting', todoDue) },
    { to: '/learning', group: 'hr', icon: 'graduation', label: t('nav.learning'), show: can('elearning.learn'), badge: learnDue, badgeLabel: tn('nav.learning_waiting', learnDue) },
    { to: '/cleaning', group: 'operations', icon: 'sparkle', label: t('nav.cleaning'), show: can('cleaning.manage') },
    { to: '/frontdesk', group: 'operations', icon: 'contact', label: t('nav.frontdesk'), show: can('frontdesk.plan') },
    { to: '/lunch', group: 'hr', icon: 'bag', label: t('nav.lunch'), show: can('lunch.order') },
    { to: '/maintenance', group: 'operations', icon: 'wrench', label: t('nav.maintenance'), show: can('maint.report') },
    { to: '/quality', group: 'operations', icon: 'badgeCheck', label: t('nav.quality'), show: can('quality.alert') },
    { to: '/repairs', group: 'operations', icon: 'hammer', label: t('nav.repairs'), show: can('repair.view') },
    { to: '/plm', group: 'operations', icon: 'branch', label: t('nav.plm'), show: can('plm.view') },
    { to: '/barcode', group: 'inventory', icon: 'scan', label: t('nav.barcode'), show: can('barcode.use') },
    { to: '/website', group: 'crm', icon: 'globe', label: t('nav.website'), show: can('website.view') },
    { to: '/events', group: 'crm', icon: 'ticket', label: t('nav.events'), show: can('events.view') },
    { to: '/appointments', group: 'crm', icon: 'calendar', label: t('nav.appointments'), show: can('appointments.view') },
    { to: '/surveys', group: 'crm', icon: 'survey', label: t('nav.surveys'), show: can('surveys.view') },
    { to: '/discuss', group: 'collab', icon: 'messages', label: t('nav.discuss'), show: can('discuss.use'), badge: discussNew, badgeLabel: tn('nav.discuss_new', discussNew) },
    { to: '/calendar', group: 'collab', icon: 'calendar', label: t('nav.calendar'), show: can('calendar.use'), badge: calendarNew, badgeLabel: tn('nav.calendar_new', calendarNew) },
    { to: '/knowledge', group: 'documents', icon: 'notebook', label: t('nav.knowledge'), show: can('knowledge.use') },
    { to: '/livechat', group: 'crm', icon: 'chat', label: t('nav.livechat'), show: can('livechat.answer'), badge: chatWaiting, badgeLabel: tn('nav.livechat_waiting', chatWaiting) },
    {
      to: '/dashboards',
      group: 'reports',
      icon: 'layout',
      label: t('nav.dashboards'),
      show: can('studio.use') && ['invoicing.view', 'reports.view', 'purchase.view', 'payroll.view', 'crm.use'].some((p) => can(p)),
    },
    {
      to: '/my-reports',
      group: 'reports',
      icon: 'list',
      label: t('nav.my_reports'),
      // For the people who work with documents and figures; the floor staff keep a short menu.
      show: can('studio.use') && ['invoicing.view', 'reports.view', 'purchase.view', 'payroll.view', 'crm.use'].some((p) => can(p)),
    },
    {
      to: '/approvals',
      group: 'purchases',
      icon: 'shieldCheck',
      label: t('nav.approvals'),
      // Shown while something waits for this person, they asked lately, or they set the rules.
      show: can('studio.use') && (approvals.waiting > 0 || approvals.asked > 0 || can('studio.manage')),
      badge: approvals.waiting,
      badgeLabel: tn('nav.approvals_waiting', approvals.waiting),
    },
    { to: '/manage/purchasing', group: 'purchases', icon: 'bag', label: t('manage.purchasing'), show: can('purchase.view') },
    { to: '/manage/stock', group: 'inventory', icon: 'box', label: t('manage.stock'), show: can('inventory.view') },
    { to: '/manage/warehouse', group: 'inventory', icon: 'layers', label: t('manage.warehouse'), show: can('inventory.view') },
    { to: '/manage/recipes', group: 'inventory', icon: 'book', label: t('manage.recipes'), show: can('inventory.view') && can('restaurant.configure') },
    { to: '/manage/automations', group: 'settings', icon: 'repeat', label: t('manage.automations'), show: can('studio.manage') },
    { to: '/ai-activity', group: 'settings', icon: 'sparkle', label: t('nav.ai_activity'), show: can('ai.audit') },
    { to: '/billing', group: 'settings', icon: 'card', label: t('nav.billing'), show: can('billing.manage') },
    { to: '/privacy', group: 'settings', icon: 'shieldCheck', label: t('nav.privacy'), show: can('privacy.manage') },
    { to: '/developer', group: 'settings', icon: 'code', label: t('nav.developer'), show: can('developer.view') },
    { to: '/manage', group: 'settings', icon: 'settings', label: t('nav.manage'), show: SECTIONS.some((s) => can(s.permission)) },
  ];
  return { entries: tabs.filter((x) => x.show), calendarSoon: calendar.soon };
}

/** Web shop orders nobody has confirmed yet, looked up every minute. */
/** Chats nobody took yet, and unread ones of the signed-in person; live, with a slow check as a fallback. */
function useChatPending(enabled: boolean) {
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!enabled) return;
    let alive = true;
    const load = () =>
      void action<{ waiting: number; unread: number }>('livechat.pending').then(
        (r) => alive && setN(r.waiting + r.unread),
        () => undefined,
      );
    load();
    const stop = subscribe(['livechat'], load);
    const id = setInterval(load, 120_000);
    return () => {
      alive = false;
      stop();
      clearInterval(id);
    };
  }, [enabled]);
  return n;
}

/** Text and WhatsApp answers nobody has read yet; live, with a slow check as a fallback. */
function useSmsPending(enabled: boolean) {
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!enabled) return;
    let alive = true;
    const load = () =>
      void action<{ unread: number }>('sms.pending').then(
        (r) => alive && setN(r.unread),
        () => undefined,
      );
    load();
    const stop = subscribe(['sms'], load);
    const id = setInterval(load, 120_000);
    return () => {
      alive = false;
      stop();
      clearInterval(id);
    };
  }, [enabled]);
  return n;
}

/** Courses I still have to finish, for the menu badge. */
function useLearningDue(enabled: boolean) {
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!enabled) return;
    let alive = true;
    const load = () =>
      void action<{ todo: number; late: number }>('elearning.due').then(
        (r) => alive && setN(r.todo),
        () => undefined,
      );
    load();
    const stop = subscribe(['elearning'], load);
    const id = setInterval(load, 300_000);
    return () => {
      alive = false;
      stop();
      clearInterval(id);
    };
  }, [enabled]);
  return n;
}

/** Sensor alerts nobody has acknowledged yet; live. */
function useIotAlerts(enabled: boolean) {
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!enabled) return;
    let alive = true;
    const load = () =>
      void action<{ open: number }>('iot.alert_count').then(
        (r) => alive && setN(r.open),
        () => undefined,
      );
    load();
    const stop = subscribe(['iot.alert'], load);
    const id = setInterval(load, 120_000);
    return () => {
      alive = false;
      stop();
      clearInterval(id);
    };
  }, [enabled]);
  return n;
}

/** My open to-dos due today or earlier, and new ones colleagues shared with me; live, with a slow check. */
function useTodoDue(enabled: boolean) {
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!enabled) return;
    let alive = true;
    const load = () =>
      void action<{ due: number; new: number }>('todo.due').then(
        (r) => alive && setN(r.due + r.new),
        () => undefined,
      );
    load();
    const stop = subscribe(['todo'], load);
    const id = setInterval(load, 300_000);
    return () => {
      alive = false;
      stop();
      clearInterval(id);
    };
  }, [enabled]);
  return n;
}

/** Social posts waiting for someone: to post by hand, to approve, or that failed; live, with a slow check. */
function useSocialPending(enabled: boolean) {
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!enabled) return;
    let alive = true;
    const load = () =>
      void action<{ total: number }>('social.pending').then(
        (r) => alive && setN(r.total),
        () => undefined,
      );
    load();
    const stop = subscribe(['social'], load);
    const id = setInterval(load, 120_000);
    return () => {
      alive = false;
      stop();
      clearInterval(id);
    };
  }, [enabled]);
  return n;
}

/** Missed calls and voicemails nobody has called back yet; live, with a slow check. */
function useCallsPending(enabled: boolean) {
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!enabled) return;
    let alive = true;
    const load = () =>
      void action<{ call_back: number }>('voip.pending').then(
        (r) => alive && setN(r.call_back),
        () => undefined,
      );
    load();
    const stop = subscribe(['voip'], load);
    const id = setInterval(load, 120_000);
    return () => {
      alive = false;
      stop();
      clearInterval(id);
    };
  }, [enabled]);
  return n;
}

/** Tickets waiting for the signed-in person, and new ones nobody took; live, with a slow check. */
function useHelpdeskPending(enabled: boolean) {
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!enabled) return;
    let alive = true;
    const load = () =>
      void action<{ total: number }>('helpdesk.pending').then(
        (r) => alive && setN(r.total),
        () => undefined,
      );
    load();
    const stop = subscribe(['helpdesk'], load);
    const id = setInterval(load, 120_000);
    return () => {
      alive = false;
      stop();
      clearInterval(id);
    };
  }, [enabled]);
  return n;
}

/** Direct messages with something new and unread mentions of the signed-in person; live, with a slow check. */
function useDiscussPending(enabled: boolean) {
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!enabled) return;
    let alive = true;
    const load = () =>
      void action<{ direct: number; mentions: number; records?: number }>('discuss.pending').then(
        (r) => alive && setN(r.direct + r.mentions + (r.records ?? 0)),
        () => undefined,
      );
    load();
    const stop = subscribe(['discuss'], load);
    const id = setInterval(load, 120_000);
    return () => {
      alive = false;
      stop();
      clearInterval(id);
    };
  }, [enabled]);
  return n;
}

/** Calendar invitations still waiting for an answer. */
function useCalendarPending(enabled: boolean) {
  const [n, setN] = useState<{ invitations: number; soon: CalendarSoon[] }>({ invitations: 0, soon: [] });
  useEffect(() => {
    if (!enabled) return;
    let alive = true;
    const load = () =>
      void action<{ invitations: number; soon: CalendarSoon[] }>('calendar.pending').then(
        (r) => alive && setN(r),
        () => undefined,
      );
    load();
    const stop = subscribe(['calendar'], load);
    const id = setInterval(load, 300_000);
    return () => {
      alive = false;
      stop();
      clearInterval(id);
    };
  }, [enabled]);
  return n;
}

function useShopPending(enabled: boolean) {
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!enabled) return;
    let alive = true;
    const load = () =>
      void action<{ new: number }>('shop.pending').then(
        (r) => alive && setN(r.new),
        () => undefined,
      );
    load();
    const id = setInterval(load, 60_000);
    return () => {
      alive = false;
      clearInterval(id);
    };
  }, [enabled]);
  return n;
}

/** A small per-person, per-device preference kept in localStorage (favourites, open groups, sidebar width). */
export function usePref<T>(key: string, initial: T): [T, (v: T) => void] {
  const { me } = useSession();
  const k = `erp.${key}.${me?.userId ?? 'anon'}`;
  const [v, setV] = useState<T>(() => {
    try {
      const raw = localStorage.getItem(k);
      return raw ? (JSON.parse(raw) as T) : initial;
    } catch {
      return initial;
    }
  });
  const set = (next: T) => {
    setV(next);
    try {
      localStorage.setItem(k, JSON.stringify(next));
    } catch {
      // A private window keeps it for this visit only.
    }
  };
  return [v, set];
}

/** The entry a path belongs to: the longest matching address wins (/manage/stock before /manage). */
export function entryFor(entries: NavEntry[], path: string): NavEntry | undefined {
  return entries.filter((e) => path === e.to || path.startsWith(`${e.to}/`)).sort((a, b) => b.to.length - a.to.length)[0];
}
